// frontend/src/lib/term.ts
//
// The app's chrome states two different Supreme Court "term" facts, and
// they used to be four separate hardcoded strings (court-stats-ot2023.ts,
// TheCourt.tsx, CasePicker.tsx, Landing.tsx) that happened to agree or
// happened not to. This module is the one place either fact is spelled
// out, so a reader who sees both screens sees one story instead of a
// contradiction.
//
//   CURRENT_TERM     - the term the simulator's calendar is set in right
//                       now. This is what the docket and the argument
//                       calendar are dated to.
//   STATISTICS_TERM  - the term the per-justice question-rate and voting
//                       statistics on /court are computed from. It tracks
//                       the data that actually exists, not the calendar:
//                       court-stats-ot2023.ts still cites OT 2023 sources, so
//                       this stays at 2023 until someone gathers newer
//                       numbers. It is behind CURRENT_TERM by however many
//                       terms the statistics have not been refreshed for,
//                       which is why the two are separate constants rather
//                       than one year and an offset.
//
// Neither year is invented here; both were already load-bearing in the
// data (court-stats-ot2023.ts cites OT 2023 sources) and in the copy (the
// docket already said October Term 2024). Do not change either year
// without updating the source citations in court-stats-ot2023.ts too.

export interface Term {
  /** Four-digit year the term opened in October, e.g. 2024. */
  year: number;
  /** Short citation form, e.g. "OT 2024". */
  short: string;
  /** Full reader-facing form, e.g. "October Term 2024". */
  label: string;
}

function term(year: number): Term {
  return { year, short: `OT ${year}`, label: `October Term ${year}` };
}

/** The term the simulator's calendar is currently in (the docket, the argument calendar). */
export const CURRENT_TERM: Term = term(2026);

/** The term the /court statistics are from. Not "the last completed term": it only moves when the data does. */
export const STATISTICS_TERM: Term = term(2023);
